import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Sentiment {
  id: string
  source: string
  total_analyzed: number
  positive_count: number
  negative_count: number
  neutral_count: number
  avg_sentiment_score: number
  sentiment_trend: string
}

interface Topic {
  id: string
  name: string
  mentions: number
  sentiment_score: number
  sentiment_label: string
  trend: string
}

interface Statistics {
  total_analyzed: number
  positive_percent: number
  negative_percent: number
  neutral_percent: number
  avg_sentiment_score: number
  topics_identified: number
  languages_supported: number
  models_deployed: number
}

export default function SentimentAnalysisPage() {
  const [sentiments, setSentiments] = useState<Sentiment[]>([])
  const [topics, setTopics] = useState<Topic[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'sources' | 'topics'>('sources')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [sentRes, topicsRes, statsRes] = await Promise.all([
        fetch('/api/sentimentanalysis/sentiments'),
        fetch('/api/sentimentanalysis/topics'),
        fetch('/api/sentimentanalysis/statistics')
      ])

      if (sentRes.ok) setSentiments(await sentRes.json())
      if (topicsRes.ok) setTopics(await topicsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching sentiment analysis data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSentimentColor = (score: number) => {
    if (score > 0.2) return 'text-green-600'
    if (score < -0.2) return 'text-red-600'
    return 'text-gray-600'
  }

  const getSentimentBadge = (label: string) => {
    const classes: Record<string, string> = {
      positive: 'bg-green-100 text-green-800',
      negative: 'bg-red-100 text-red-800',
      neutral: 'bg-gray-100 text-gray-800'
    }
    return classes[label] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Sentiment Analysis</h1>
          <p className="mt-2 text-gray-600">AI-powered sentiment analysis and opinion mining</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Analyzed</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_analyzed.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Positive</p>
              <p className="text-2xl font-bold text-green-600">{(statistics.positive_percent * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Negative</p>
              <p className="text-2xl font-bold text-red-600">{(statistics.negative_percent * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Topics Identified</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.topics_identified}</p>
            </div>
          </div>
        )}

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('sources')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'sources' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              By Source ({sentiments.length})
            </button>
            <button
              onClick={() => setActiveTab('topics')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'topics' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Topics ({topics.length})
            </button>
          </nav>
        </div>

        {activeTab === 'sources' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sentiments.map((sent) => (
              <div key={sent.id} className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 capitalize">{sent.source.replace('_', ' ')}</h3>
                <p className="text-sm text-gray-500 mt-1">{sent.total_analyzed.toLocaleString()} analyzed</p>
                <div className="mt-4 flex justify-around">
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{sent.positive_count}</p>
                    <p className="text-xs text-gray-500">Positive</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-600">{sent.neutral_count}</p>
                    <p className="text-xs text-gray-500">Neutral</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-red-600">{sent.negative_count}</p>
                    <p className="text-xs text-gray-500">Negative</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Avg Score</span>
                    <span className={`text-sm font-medium ${getSentimentColor(sent.avg_sentiment_score)}`}>
                      {sent.avg_sentiment_score.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-sm text-gray-500">Trend</span>
                    <span className="text-sm font-medium capitalize">{sent.sentiment_trend}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'topics' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Topic</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mentions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sentiment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {topics.map((topic) => (
                  <tr key={topic.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{topic.name}</td>
                    <td className="px-6 py-4 text-gray-500">{topic.mentions.toLocaleString()}</td>
                    <td className={`px-6 py-4 font-medium ${getSentimentColor(topic.sentiment_score)}`}>
                      {topic.sentiment_score.toFixed(2)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getSentimentBadge(topic.sentiment_label)}`}>
                        {topic.sentiment_label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{topic.trend === 'up' ? '↑' : topic.trend === 'down' ? '↓' : '→'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
